// node_modules/financial-arithmetic-functions/index.mjs
var allDigits = /^(-|\+)?\d+$/;
var withDecimal = /^(-|\+)?(\d+)\.(\d+)$/;
function validate(str) {
  return typeof str === `string` && (allDigits.test(str) || withDecimal.test(str));
}
function validateAndThrow(str) {
  if (!validate(str)) {
    throw new Error(`Invalid input ` + str);
  }
}
function getPrecision(str) {
  if (allDigits.test(str)) {
    return 0;
  } else {
    return str.split(`.`)[1].length;
  }
}
function add(a, b) {
  validateAndThrow(a);
  validateAndThrow(b);
  const normalized = normalizeToSamePrecision(a, b);
  const sum = (BigInt(normalized.a) + BigInt(normalized.b)).toString();
  return normalized.precision > 0 ? addDecimal(sum, normalized.precision) : sum;
}
function subtract(a, b) {
  validateAndThrow(a);
  validateAndThrow(b);
  const normalized = normalizeToSamePrecision(a, b);
  const result = (BigInt(normalized.a) - BigInt(normalized.b)).toString();
  return normalized.precision > 0 ? addDecimal(result, normalized.precision) : result;
}
function multiply(a, b) {
  validateAndThrow(a);
  validateAndThrow(b);
  const normalized = normalizeToLargeEnoughPrecisionToMultiply(a, b);
  const result = (BigInt(normalized.a) * BigInt(normalized.b)).toString();
  return normalized.precision > 0 ? addDecimal(result, normalized.precision) : result;
}
function modulo(dividend, divisor) {
  validateAndThrow(dividend);
  validateAndThrow(divisor);
  const normalized = normalizeToSamePrecision(dividend, divisor);
  const result = (BigInt(normalized.a) % BigInt(normalized.b)).toString();
  return normalized.precision > 0 ? addDecimal(result, normalized.precision) : result;
}
function normalizeToSamePrecision(a, b) {
  const precisionA = getPrecision(a);
  const precisionB = getPrecision(b);
  const differenceInPrecisions = diff(precisionA, precisionB);
  const precision = Math.max(precisionA, precisionB);
  if (precisionA > precisionB) {
    b = padWithZeroes(b, differenceInPrecisions);
  } else if (precisionB > precisionA) {
    a = padWithZeroes(a, differenceInPrecisions);
  }
  return {
    a: stripDecimal(a),
    b: stripDecimal(b),
    precision
  };
}
function normalizeToLargeEnoughPrecisionToMultiply(a, b) {
  const precisionA = getPrecision(a);
  const precisionB = getPrecision(b);
  const precision = precisionA + precisionB;
  return {
    a: stripDecimal(a),
    b: stripDecimal(b),
    precision
  };
}
function diff(a, b) {
  return a > b ? a - b : b - a;
}
function padWithZeroes(str, newZeroes) {
  while (newZeroes > 0) {
    str += `0`;
    newZeroes--;
  }
  return str;
}
function stripDecimal(str) {
  return str.replace(`.`, ``);
}
function addDecimal(str, position) {
  const isNegative = str[0] === `-`;
  if (isNegative) {
    str = str.substring(1);
  }
  while (str.length < position + 1) {
    str = `0` + str;
  }
  const beforeTheDecimal = str.length - position;
  let newNumber = str.substring(0, beforeTheDecimal) + `.` + str.substring(beforeTheDecimal);
  if (isNegative) {
    newNumber = `-` + newNumber;
  }
  return newNumber;
}

// with-default-rounding-strategy.ts
var allZeroesRegex = /^0(\.0+)?$/;
var someNonZeroDigit = /[1-9]/;
var makeArgumentString = (fn) => (input) => {
  const input_as_string = typeof input === `string` ? input : input.toString();
  if (!validate(input_as_string)) {
    throw new Error(`Invalid input "${input}"`);
  }
  return fn(input_as_string);
};
var make_financial_number = (defaultRoundingStrategy) => {
  if (typeof defaultRoundingStrategy !== `function`) {
    throw new Error(`That doesn't look like a valid rounding strategy (it's not a function)`);
  }
  const makeNumberObject = makeArgumentString((str) => {
    const self = {
      plus: makeArgumentString((otherSide) => makeNumberObject(add(str, otherSide))),
      minus: makeArgumentString((otherSide) => makeNumberObject(subtract(str, otherSide))),
      times: makeArgumentString((otherSide) => makeNumberObject(multiply(str, otherSide))),
      mod: makeArgumentString((otherSide) => makeNumberObject(modulo(str, otherSide))),
      getPrecision() {
        return getPrecision(str);
      },
      toJSON() {
        return str;
      },
      toString(precision, roundingStrategy) {
        if (typeof precision === `number`) {
          return self.changePrecision(precision, roundingStrategy).toString();
        }
        return str;
      },
      changePrecision(precision, roundingStrategy) {
        roundingStrategy = roundingStrategy || defaultRoundingStrategy;
        return makeNumberObject(trimNegationFromZero(roundingStrategy(makeNumberObject(str), precision)));
      },
      isNegative() {
        return self.toString()[0] === `-`;
      },
      equal(otherSide) {
        const difference = makeNumberObject(otherSide).minus(self).toString();
        return allZeroesRegex.test(difference);
      },
      gt(otherSide) {
        const difference = self.minus(otherSide);
        return !difference.isNegative() && someNonZeroDigit.test(difference.toString()) && !self.equal(otherSide);
      },
      gte(otherSide) {
        return self.gt(otherSide) || self.equal(otherSide);
      },
      lt(otherSide) {
        return !self.gte(otherSide);
      },
      lte(otherSide) {
        return !self.gt(otherSide);
      }
    };
    return self;
  });
  return makeNumberObject;
};
function trimNegationFromZero(str) {
  if (str.length >= 2 && str[0] === `-`) {
    const restOfNumberAfterDash = str.substring(1);
    return allZeroesRegex.test(restOfNumberAfterDash) ? restOfNumberAfterDash : str;
  }
  return str;
}
var with_default_rounding_strategy_default = make_financial_number;

// index.ts
function adjustPrecisionByRounding(number, targetPrecision) {
  const currentPrecision = number.getPrecision();
  const precisionIsDropping = targetPrecision < currentPrecision;
  if (precisionIsDropping) {
    const operation = number.isNegative() ? number.minus : number.plus;
    number = operation(`0.` + zeroes(targetPrecision) + `5`);
  }
  return adjustPrecisionByTrimming(number, targetPrecision);
}
function adjustPrecisionByTrimming(number, targetPrecision) {
  const currentPrecision = number.getPrecision();
  const str = number.toString();
  if (currentPrecision === targetPrecision) {
    return str;
  } else if (targetPrecision === 0) {
    return str.split(`.`)[0];
  } else if (currentPrecision === 0) {
    return str + `.` + zeroes(targetPrecision);
  } else if (targetPrecision < currentPrecision) {
    const charactersToRemove = currentPrecision - targetPrecision;
    return str.substring(0, str.length - charactersToRemove);
  } else if (currentPrecision < targetPrecision) {
    const zeroesToAdd = targetPrecision - currentPrecision;
    return str + zeroes(zeroesToAdd);
  }
}
function zeroes(times) {
  let output = ``;
  for (let i = 0; i < times; ++i) {
    output += `0`;
  }
  return output;
}
var default_export = Object.assign(with_default_rounding_strategy_default(adjustPrecisionByTrimming), {
  trim: adjustPrecisionByTrimming,
  round: adjustPrecisionByRounding
});
var financial_number_default = default_export;
export {
  financial_number_default as default,
  adjustPrecisionByRounding as round,
  adjustPrecisionByTrimming as trim,
  with_default_rounding_strategy_default as withDefaultRoundingStrategy
};
